import {put, call} from 'redux-saga/effects';
import ActivityCategory from "../../entities/activityCategory";
import * as Actions from "../actions/activityCategoryActions";
import * as idb from '../../lib/idbd';
import * as api from '../../rest/api';
import {processQueue} from "../../queues/processQueue";
import newHRTimestamp from "../../lib/picos/timestamp";

// Inlined to reduce circular dependencies
function getCurrentUserId() {
    return sessionStorage.getItem('userId');
}

// Inlined to reduce circular dependencies
function getCurrentFirmId() {
    return sessionStorage.getItem('firmId');
}

type ActivityCategoryAction = {
    activityCategoryId: string;
    activityCategory: ActivityCategory;
    activityCategories: ActivityCategory[];
};

export function* fetchOneActivityCategory(action: ActivityCategoryAction) {
	try {
		const activityCategory: ActivityCategory = yield call(api.findOne, 'activityCategory', action.activityCategoryId);

		// Now we can update the user interface
		yield put({type: Actions.ACTIVITYCATEGORY_FETCH_ONE_SUCCESS, activityCategoryId: action.activityCategoryId, activityCategory});
	}
	catch(err) {
		yield put({type: Actions.ACTIVITYCATEGORY_FETCH_ONE_FAILURE, activityCategoryId: action.activityCategoryId, status: 500, err});

		console.error('Error fetching from database', err);
	}
}

/**
 * @param {ActivityCategoryAction} action
 */
export function* saveActivityCategory(action: ActivityCategoryAction) {
    // console.debug('⮂ ACTIVITYCATEGORY_SAVE saga', action.activityCategory?.id);
    
	if (action.activityCategory === undefined || action.activityCategory === null) {
		console.error('saveActivityCategory received a null');
		throw new Error('saveActivityCategory received a null');
	}

	if (!(action.activityCategory instanceof ActivityCategory)) {
		action.activityCategory = new ActivityCategory(action.activityCategory);
	}

	try {
		// Save to IndexedDB
		const addResult = yield call(idb.add, 'activityCategory', action.activityCategory);
		if (addResult.isErr()) {
			console.error(`⮂ saveActivityCategory: IDB write failed: ${addResult.unwrapErr().message}`);
			yield put({type: Actions.ACTIVITYCATEGORY_SAVE_FAILURE, activityCategory: action.activityCategory, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.ACTIVITYCATEGORY_SAVE, activityCategory: action.activityCategory});

		// Delete this entry from the cache (if there is one). Use uncacheByEntityId so we
		// catch entries regardless of which parentId the writer used (zero for grid views,
		// topLevelId for sub-task views, etc.) — the prior uncacheItem call passed entityId
		// as parentId, which never matched what writers store under, so entries leaked.
		yield call(window.storageWorker.uncacheByEntityId, getCurrentFirmId(), getCurrentUserId(), 'activityCategory', action.activityCategory.id);

		// Let the queue know to process
		yield call(processQueue);
	}
	catch(err) {
		yield put({type: Actions.ACTIVITYCATEGORY_SAVE_FAILURE, activityCategory: action.activityCategory, status: 500, err});

		console.error('Error saving to database', err);
	}
}

/**
 * TODO: Update for saveMany
 * @param {ActivityCategoryAction} action
 * @param {ActivityCategory[]} action.activityCategories
 */
export function* saveManyActivityCategories(action: ActivityCategoryAction) {
    // console.debug('⮂ ACTIVITYCATEGORY_SAVE_MANY saga', action.activityCategories.map(activityCategory => activityCategory.id).join(','));
    
    if (!Array.isArray(action.activityCategories)) {
        console.error('saveManyActivityCategories expected an array, received', action.activityCategories);
        throw new Error('saveManyActivityCategories expected an array');
    }

	try {
		const toSave: ActivityCategory[] = [];

		// Save each item to IndexedDB
		for(let i = 0; i < action.activityCategories.length; i++) {
			const activityCategory = action.activityCategories[i];
			activityCategory.persisted = false;
			toSave.push(activityCategory);
		}

		const addResult = yield call(idb.add, 'activityCategory', toSave);
		if (addResult.isErr()) {
			console.error(`⮂ saveManyActivityCategories: IDB write failed: ${addResult.unwrapErr().message}`);
			yield put({type: Actions.ACTIVITYCATEGORY_SAVE_MANY_FAILURE, activityCategories: action.activityCategories, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.ACTIVITYCATEGORY_SAVE_MANY, activityCategories: toSave});

		// Let the queue know to process
		yield call(processQueue)
	}
	catch(err) {
		yield put({type: Actions.ACTIVITYCATEGORY_SAVE_MANY_FAILURE, activityCategories: action.activityCategories, status: 500, err});

		console.error(`Error saving ${action.activityCategories.length} activityCategories to database`, err);
	}
}

/**
 * @param {ActivityCategoryAction} action
 */
export function* removeActivityCategory(action: ActivityCategoryAction) {
    const userId = getCurrentUserId();
	if (!userId || action.activityCategory === undefined || action.activityCategory === null || !(action.activityCategory instanceof ActivityCategory)) {
			console.error('removeActivityCategory expected type ActivityCategory, received', action.activityCategory);
			throw new Error('removeActivityCategory expected type ActivityCategory');
		}

	try {
		// Mark as deleted
		action.activityCategory.status = 'D';
		action.activityCategory.addedOn = newHRTimestamp();
		action.activityCategory.addedBy = userId;

		// Save to IndexedDB
		const addResult = yield call(idb.add, 'activityCategory', action.activityCategory);
		if (addResult.isErr()) {
			console.error(`⮂ removeActivityCategory: IDB write failed: ${addResult.unwrapErr().message}`);
			yield put({type: Actions.ACTIVITYCATEGORY_REMOVE_FAILURE, activityCategory: action.activityCategory, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.ACTIVITYCATEGORY_REMOVE, activityCategory: action.activityCategory});

		// Let the queue know to process
		yield call(processQueue)
	}
	catch(err) {
		yield put({type: Actions.ACTIVITYCATEGORY_REMOVE_FAILURE, activityCategory: action.activityCategory, status: 500, err});

		console.error('Error deleting from database', err);
	}
}

/**
	* Restore the last version of this ActivityCategory.
	*
	* @param {ActivityCategoryAction} action
	*/
export function* restoreActivityCategory(action: ActivityCategoryAction) {
    const userId = getCurrentUserId();
    if (!userId) {
        console.error('restoreActivityCategory expected getCurrentUserId() to return a value');
        return;
    }
    
	if (action.activityCategory === undefined || action.activityCategory === null) {
        console.error('restoreActivityCategory expected a ActivityCategory, received null');
        throw new Error('restoreActivityCategory expected a ActivityCategory, received null');
    }
    else if (!(action.activityCategory instanceof ActivityCategory)) action.activityCategory = new ActivityCategory(action.activityCategory); 
    
	try {
		// Mark as restored
		action.activityCategory.status = 'U';
		action.activityCategory.addedOn = newHRTimestamp();
		action.activityCategory.addedBy = userId;
		action.activityCategory.restore = true;

		// Save to IndexedDB
		const addResult = yield call(idb.add, 'activityCategory', action.activityCategory);
		if (addResult.isErr()) {
			console.error(`⮂ restoreActivityCategory: IDB write failed: ${addResult.unwrapErr().message}`);
			yield put({type: Actions.ACTIVITYCATEGORY_RESTORE_FAILURE, activityCategory: action.activityCategory, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.ACTIVITYCATEGORY_RESTORE, activityCategory: action.activityCategory});
	}
	catch(err) {
		yield put({type: Actions.ACTIVITYCATEGORY_RESTORE_FAILURE, activityCategory: action.activityCategory, status: 500, err});

		console.error('Error restoring from database', err);
	}
}